import type { CustomerOrder } from '../../customerOrdersApi'
import type { ShopMessageKey } from '../../../i18n'
import { formatCurrency } from '../../helpers/formatters'
import { CustomerAreaSurfaceCard, SkeletonBlock } from '../customer-area'
import {
  CalendarIcon,
  CurrencyIcon,
  OrderIcon,
  StatusIcon,
  resolveOrderStatusBadgeClass,
  resolveOrderStatusLabel,
} from './preferencesShared'

type LastPaymentSectionProps = {
  ordersLoading: boolean
  latestPaymentOrder: CustomerOrder | null
  latestPaymentDate: string
  locale: string
  language: 'it' | 'en'
  surfaceCardClassName: string
  paymentMetaCardClassName: string
  onNavigate: (targetPath: string) => void
  toLocalizedPath: (path: string) => string
  t: (key: ShopMessageKey, params?: Record<string, string | number>) => string
}

export const LastPaymentSection = ({
  ordersLoading,
  latestPaymentOrder,
  latestPaymentDate,
  locale,
  language,
  surfaceCardClassName,
  paymentMetaCardClassName,
  onNavigate,
  toLocalizedPath,
  t,
}: LastPaymentSectionProps) => {
  if (!ordersLoading && !latestPaymentOrder) {
    return null
  }

  return (
    <CustomerAreaSurfaceCard
      title={t('customer.preferences.lastPayment.title')}
      subtitle={t('customer.preferences.lastPayment.subtitle')}
      className={surfaceCardClassName}
      contentClassName="mt-4"
    >
      {ordersLoading ? (
        <div className="space-y-2">
          <SkeletonBlock className="h-10 rounded-xl" />
          <SkeletonBlock className="h-10 rounded-xl" />
        </div>
      ) : latestPaymentOrder ? (
        <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
          <article className={paymentMetaCardClassName}>
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-(--color-border-default) bg-(--color-bg-surface-muted) text-(--color-text-muted)">
              <CurrencyIcon className="h-3.5 w-3.5" />
            </span>
            <p className="mt-2 text-[11px] font-medium tracking-wide text-(--color-text-muted)">
              {t('customer.preferences.lastPayment.amount')}
            </p>
            <p className="mt-1 text-sm font-semibold text-(--color-text-primary)">
              {formatCurrency(latestPaymentOrder.total, locale, latestPaymentOrder.currency)}
            </p>
          </article>
          <article className={paymentMetaCardClassName}>
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-(--color-border-default) bg-(--color-bg-surface-muted) text-(--color-text-muted)">
              <CalendarIcon className="h-3.5 w-3.5" />
            </span>
            <p className="mt-2 text-[11px] font-medium tracking-wide text-(--color-text-muted)">
              {t('customer.preferences.lastPayment.date')}
            </p>
            <p className="mt-1 text-sm font-semibold text-(--color-text-primary)">{latestPaymentDate}</p>
          </article>
          <article className={paymentMetaCardClassName}>
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-(--color-border-default) bg-(--color-bg-surface-muted) text-(--color-text-muted)">
              <StatusIcon className="h-3.5 w-3.5" />
            </span>
            <p className="mt-2 text-[11px] font-medium tracking-wide text-(--color-text-muted)">
              {t('customer.preferences.lastPayment.status')}
            </p>
            <span
              className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${resolveOrderStatusBadgeClass(
                latestPaymentOrder.status,
              )}`}
            >
              {resolveOrderStatusLabel(latestPaymentOrder.status, language)}
            </span>
          </article>
          <article className={paymentMetaCardClassName}>
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-(--color-border-default) bg-(--color-bg-surface-muted) text-(--color-text-muted)">
              <OrderIcon className="h-3.5 w-3.5" />
            </span>
            <p className="mt-2 text-[11px] font-medium tracking-wide text-(--color-text-muted)">
              {t('customer.preferences.lastPayment.order')}
            </p>
            <button
              type="button"
              onClick={() =>
                onNavigate(toLocalizedPath(`/dashboard/orders/${encodeURIComponent(latestPaymentOrder.id)}`))
              }
              className="ca-theme-transition mt-1 inline-flex text-sm font-semibold text-(--color-text-primary) underline decoration-(--color-border-strong) underline-offset-2 hover:text-(--color-text-secondary)"
            >
              {latestPaymentOrder.orderNumber}
            </button>
          </article>
        </div>
      ) : null}
    </CustomerAreaSurfaceCard>
  )
}
